'use client';
import { jsx as _jsx } from "react/jsx-runtime";
import config from '@haaretz/l-config';
import usePlatform from '@haaretz/s-atoms/platform';
import isNextLink from '@haaretz/s-url-utils/isNextLink';
import { usePathname as useAppPathname, useRouter as useAppRouter } from 'next/navigation';
import { useRouter as usePagesRouter } from 'next/router';
import React from 'react';
const PAGE_LOADER_DELAY = config.get('pageLoaderDelay');
function isAppRouter() {
    try {
        // eslint-disable-next-line react-hooks/rules-of-hooks
        useAppRouter();
        return true;
    } catch  {
        return false;
    }
}
function usePagesPathname() {
    const router = usePagesRouter();
    return router.pathname;
}
export const PageLoaderContext = /*#__PURE__*/ React.createContext(undefined);
export const PageLoaderContextUpdater = /*#__PURE__*/ React.createContext(undefined);
export default function PageLoaderProvider({ children }) {
    const isAppRoutingEnabled = isAppRouter();
    const [useRouter, usePathname] = isAppRoutingEnabled ? [
        useAppRouter,
        useAppPathname
    ] : [
        usePagesRouter,
        usePagesPathname
    ];
    const [isPageLoading, setIsPageLoading] = React.useState(false);
    const [nextUrl, setNextUrl] = React.useState(null);
    const [isPageLoadingDelayed, setIsPageLoadingDelayed] = React.useState(false);
    const timeoutRef = React.useRef(null);
    const relatedTimeoutUrlRef = React.useRef(null);
    // eslint-disable-next-line react-compiler/react-compiler
    const pathname = usePathname();
    // eslint-disable-next-line react-compiler/react-compiler
    const router = useRouter();
    const platform = usePlatform();
    function clearPageLoadingTimeout() {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }
        timeoutRef.current = null;
        relatedTimeoutUrlRef.current = null;
    }
    const setIsPageLoadingCb = React.useCallback((isLoading)=>{
        clearPageLoadingTimeout();
        setIsPageLoading(isLoading);
        if (isLoading && PAGE_LOADER_DELAY) {
            relatedTimeoutUrlRef.current = document.location.href;
            timeoutRef.current = setTimeout(()=>{
                if (document.location.href === relatedTimeoutUrlRef.current) {
                    setIsPageLoadingDelayed(isLoading);
                }
                clearPageLoadingTimeout();
            }, PAGE_LOADER_DELAY);
        } else {
            setIsPageLoadingDelayed(isLoading);
            clearPageLoadingTimeout();
        }
    }, [
        setIsPageLoadingDelayed
    ]);
    const tryGoToAgain = React.useCallback(function tryGoToAgain() {
        if ('onLine' in navigator && navigator.onLine && nextUrl) {
            if (isNextLink(nextUrl, platform)) {
                setNextUrl(null);
                router.push(nextUrl);
                setIsPageLoadingCb(true);
            } else {
                window.location.assign(nextUrl);
            }
        }
    }, [
        nextUrl,
        platform,
        router,
        setIsPageLoadingCb
    ]);
    React.useEffect(()=>{
        clearPageLoadingTimeout();
        setIsPageLoading(false);
        setIsPageLoadingDelayed(false);
    }, [
        pathname
    ]);
    React.useEffect(()=>()=>{
            clearPageLoadingTimeout();
        }, []);
    React.useEffect(()=>{
        const onOnlineHandler = ()=>{
            tryGoToAgain();
        };
        window.addEventListener('online', onOnlineHandler);
        return ()=>{
            window.removeEventListener('online', onOnlineHandler);
        };
    }, [
        nextUrl,
        router,
        setIsPageLoadingCb,
        tryGoToAgain
    ]);
    const pageLoaderValue = React.useMemo(()=>({
            isLoading: isPageLoading,
            isLoadingDelayed: isPageLoadingDelayed,
            nextUrl
        }), [
        isPageLoading,
        isPageLoadingDelayed,
        nextUrl
    ]);
    const pageLoaderUpdaterValue = React.useMemo(()=>({
            togglePageLoader: setIsPageLoadingCb,
            setNextUrl,
            tryGoToAgain
        }), [
        setIsPageLoadingCb,
        tryGoToAgain
    ]);
    return /*#__PURE__*/ _jsx(PageLoaderContext, {
        value: pageLoaderValue,
        children: /*#__PURE__*/ _jsx(PageLoaderContextUpdater, {
            value: pageLoaderUpdaterValue,
            children: children
        })
    });
}
export function usePageLoader() {
    const context = React.useContext(PageLoaderContext);
    if (context === undefined) {
        throw new Error('usePageLoaderContext must be used within a PageLoaderProvider.');
    }
    return context;
}
export function usePageLoaderUpdater() {
    const setContext = React.useContext(PageLoaderContextUpdater);
    if (setContext === undefined) {
        throw new Error('usePageLoaderContextUpdater must be used within a PageLoaderProvider.');
    }
    return setContext;
}

//# sourceMappingURL=PageLoaderProvider.js.map